<template>
  <div class="pagination">
    <div class="flex flex-1 justify-between sm:hidden">
      <a href="#" class="pagination-button-previus" @click.prevent="previousPage">Previous</a>
      <a href="#" class="pagination-button-next" @click.prevent="nextPage">Next</a>
    </div>
    <div class="hidden sm:flex sm:flex-1 sm:items-center sm:justify-between">
      <div>
        <p class="text-sm text-gray-700">
          Showing
          <span class="font-medium">1</span>
          to
          <span class="font-medium">{{ props.pagination.perPage }}</span>
          of
          <span class="font-medium">{{ props.pagination.total }}</span>
          results
        </p>
      </div>
      <div>
        <nav class="isolate inline-flex -space-x-px rounded-md shadow-xs" aria-label="Pagination">
          <a href="#" class="pagination-button-previus" @click.prevent="previousPage">
            <span class="sr-only">Previous</span>
            <ArrowIcon class="rotate-90 size-5" aria-hidden="true" />
          </a>
          <template v-for="page in pageNumbers" :key="page">
            <a
              v-if="page !== '...'"
              href="#"
              class="pagination-link"
              :class="{ 'pagination-link-active': page === props.pagination.page }"
              @click.prevent="emit('update:page', Number(page))"
            >
              {{ page }}
            </a>
            <span
              v-else
              class="pagination-link"
            >
              ...
            </span>
          </template>
          <a href="#" class="pagination-button-next" @click.prevent="nextPage">
            <span class="sr-only">Next</span>
            <ArrowIcon class="-rotate-90 size-5" aria-hidden="true" />
          </a>
        </nav>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue';

import ArrowIcon from '@/components/icons/ArrowIcon.vue';

import { PaginationI } from '@/interfaces/interfaces';

const props = defineProps<{
  pagination: PaginationI;
}>();

const emit = defineEmits<{
  (e: 'update:page', page: number): void
}>();

const pageNumbers = computed(() => {
  const lastPage = props.pagination.lastPage;
  const pages: (number | string)[] = Array.from({ length: lastPage }, (_, i) => i + 1);

  if (lastPage > 6) {
    const firstThree = pages.splice(0, 3);
    const lastThree = pages.splice(-3);
    return [...firstThree, '...', ...lastThree];
  }

  return pages;
});

const previousPage = () => {
  if (props.pagination.page > 1) {
    emit('update:page', props.pagination.page - 1);
  }
};

const nextPage = () => {
  if (props.pagination.page < props.pagination.lastPage) {
    emit('update:page', props.pagination.page + 1);
  }
};
</script>

<style scoped>
.pagination {
  @apply flex items-center justify-between border-t border-gray-200 bg-white px-4 py-3 sm:px-6;
}

.pagination-button-previus {
  @apply relative inline-flex items-center rounded-l-md px-2 py-2 text-gray-400 ring-1 ring-gray-300 ring-inset hover:bg-gray-50;
}

.pagination-button-next {
  @apply relative inline-flex items-center rounded-r-md px-2 py-2 text-gray-400 ring-1 ring-gray-300 ring-inset hover:bg-gray-50;
}

.pagination-link {
  @apply inline-flex items-center px-4 py-2 ring-1 ring-gray-300 ring-inset bg-white text-sm font-semibold text-primary-dark hover:bg-gray-50 focus:z-20 focus-visible:outline-2 focus-visible:outline-offset-2;
}

.pagination-link-active {
  @apply bg-primary-main text-white hover:bg-primary-main;
}
</style>
